// ======================================================================
// File: src/articles/WaecEnglishGuide.jsx
// Article: WAEC English Language – Complete Master Guide
// ======================================================================

import { Typography, Divider } from "@mui/material";


export default function WaecEnglishGuide() {
  return (
    <>
      {/* INTRO */}
      <Typography sx={{ mb: 2 }}>
        English Language is one of the most important subjects in WAEC because
        it determines whether a student will gain admission or not. A strong
        credit in English opens the door to all courses, while a weak grade
        blocks many opportunities.
      </Typography>

      <Typography sx={{ mb: 3 }}>
        This comprehensive guide explains everything you need to know to prepare
        effectively — the exam structure, high-yield topics, question patterns,
        scoring strategy, and study techniques. Follow it carefully to improve
        your performance and build confidence before the exam.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* SECTION 1 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        1. Understanding the WAEC English Exam Structure
      </Typography>

      <Typography sx={{ mt: 2 }}>
        WAEC English consists of three major components:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li><strong>Paper 1 – Objective Test</strong> (60 questions)</li>
        <li><strong>Paper 2 – Essay</strong> (Letter writing, speech, narrative)</li>
        <li><strong>Paper 3 – Test of Orals</strong> (for schools)</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Most students struggle because they do not understand the weight of each
        section. Paper 1 alone carries a large portion of the score, but Paper 2
        often determines whether you get **A, B, C, or D**.
      </Typography>

      {/* SECTION 2 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        2. Key Areas to Master for WAEC English
      </Typography>

      <Typography sx={{ mt: 2, fontWeight: 700 }}>
        To score high, focus on these areas:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Comprehension passages</li>
        <li>Summary writing</li>
        <li>Lexis and structure</li>
        <li>Synonyms and antonyms</li>
        <li>Registers</li>
        <li>Oral English basics</li>
        <li>Punctuation</li>
        <li>Continuous writing (letter, speech, narrative, article)</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        WAEC repeats these areas every year, especially **comprehension,
        summary, and lexis**.
      </Typography>

      {/* SECTION 3 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        3. How to Approach Comprehension Passages
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Comprehension carries heavy marks, and WAEC tests:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Main ideas</li>
        <li>Supporting details</li>
        <li>Inference</li>
        <li>Vocabulary in context</li>
        <li>True or false interpretation</li>
      </Typography>

      <Typography sx={{ mt: 2, mb: 3 }}>
        <strong>Tip:</strong> Read the questions first before reading the passage.
        This helps you locate answers faster and avoid reading multiple times.
      </Typography>

      {/* SECTION 4 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        4. Mastering Summary Writing
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Summary questions test your ability to:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Identify key points</li>
        <li>Avoid repetition</li>
        <li>Use your own words</li>
        <li>Write short, direct sentences</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        WAEC requires <strong>precise answers</strong>. If you write long or
        vague sentences, you lose marks. Extract the idea, keep it short, and
        avoid examples or illustrations.
      </Typography>

      {/* SECTION 5 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        5. Lexis and Structure – The Most Scored Section
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Lexis and Structure contributes a major part of the objective paper.
        Focus on:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Contextual meaning</li>
        <li>Sentence interpretation</li>
        <li>Idioms and expressions</li>
        <li>Phrasal verbs</li>
        <li>Concord (subject–verb agreement)</li>
        <li>Prepositions</li>
        <li>Spotting errors</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        If you master this section, your overall score increases massively.
      </Typography>

      {/* SECTION 6 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        6. Oral English – Basics You Must Know
      </Typography>

      <Typography sx={{ mt: 2 }}>
        WAEC focuses on these oral English areas:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Vowel sounds</li>
        <li>Consonant sounds</li>
        <li>Stress patterns</li>
        <li>Intonation</li>
        <li>Homophones</li>
        <li>Minimal pairs</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        You don’t need to memorize the entire phonetic alphabet — just learn how
        vowel and consonant sounds are produced and when stress falls on a
        syllable.
      </Typography>

      {/* SECTION 7 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        7. Essay Writing – Your Ticket to an A
      </Typography>

      <Typography sx={{ mt: 2 }}>
        WAEC often tests:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Letter writing (formal/informal)</li>
        <li>Article writing</li>
        <li>Speech writing</li>
        <li>Descriptive writing</li>
        <li>Narrative essays</li>
      </Typography>

      <Typography sx={{ mt: 2, mb: 3 }}>
        Focus on:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Correct format</li>
        <li>Clear introduction</li>
        <li>Organized paragraphs</li>
        <li>Grammar accuracy</li>
        <li>Logical flow</li>
        <li>Avoiding slang</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        A neat, well-structured essay can raise your grade significantly.
      </Typography>

      {/* SECTION 8 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        8. How to Prepare Effectively for WAEC English
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3, mt: 2 }}>
        <li>Use recommended WAEC English textbooks</li>
        <li>Practice 30–50 objective questions daily</li>
        <li>Read wide (articles, novels, newspapers)</li>
        <li>Practice summary writing weekly</li>
        <li>Revise basic grammar rules</li>
        <li>Join study groups for discussion</li>
        <li>Attempt past questions under timed conditions</li>
      </Typography>

      {/* SECTION 9 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        9. Common Mistakes Students Make
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Ignoring essay writing practice</li>
        <li>Skipping Oral English topics</li>
        <li>Reading comprehension too quickly</li>
        <li>Not reviewing past questions</li>
        <li>Using social-media style writing in essays</li>
      </Typography>

      {/* ENDING */}
      <Divider sx={{ my: 4 }} />

      <Typography sx={{ mb: 2 }}>
        Mastering WAEC English requires consistent practice, not cramming. Focus
        on comprehension, summary, lexis, and essay writing. With these
        strategies, you will build the confidence and skills needed to score
        high.
      </Typography>

      <Typography>
        Start practicing English CBT questions now on CBT Master to strengthen
        your preparation.
      </Typography>
	  
	  
    </>
  );
}
